/**
 * Text validation helpers shared by contact/lead forms (client) and their
 * API routes (server).
 *
 * The site only serves English-speaking customers, and virtually all form
 * spam arrives written in non-Latin scripts (Cyrillic, CJK, Arabic, ...).
 * These helpers detect letters from non-Latin scripts so forms can warn the
 * visitor and block the submission.
 *
 * Accented Latin letters (é, ñ, ü, ...) are still allowed so legitimate
 * names like "José" or "Müller" are never rejected.
 */

// Matches any letter that is NOT in the Latin script. Built with the RegExp
// constructor (not a literal) because Unicode property escapes require an
// ES2018 regex target and tsconfig targets ES2017; V8 (Edge runtime +
// browsers) supports them at runtime regardless.
const NON_LATIN_LETTER = new RegExp('(?!\\p{Script=Latin})\\p{L}', 'u');

/** Standard warning shown to the user when non-English text is detected. */
export const NON_ENGLISH_TEXT_ERROR =
  'Please use English (Latin) characters only. We are unable to process messages written in other alphabets.';

/**
 * Returns true if the text contains letters from a non-Latin script
 * (Cyrillic, Chinese, Arabic, Hebrew, Greek, etc.).
 *
 * Latin letters (including accented ones), digits, punctuation, whitespace,
 * and symbols are all allowed.
 */
export function containsNonLatinText(text: string): boolean {
  if (!text) return false;
  return NON_LATIN_LETTER.test(text);
}

/**
 * Checks a map of field label → value and returns the label of the first
 * field containing non-Latin text, or null if all fields are clean.
 * Skips empty/undefined values.
 */
export function findNonLatinField(
  fields: Record<string, string | undefined | null>
): string | null {
  for (const [label, value] of Object.entries(fields)) {
    if (value && containsNonLatinText(value)) {
      return label;
    }
  }
  return null;
}
